import React, { useMemo, useCallback } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Linking } from 'react-native';
import { Text } from '@/components/common/text';
import { useRoute } from '@react-navigation/native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

export const RoommateDetailScreen: React.FC = () => {
  const route = useRoute<any>();
  const styles = useThemedStyles(makeStyles);
  const roommate = route.params?.roommate;

  const first = roommate?.data?.account?.firstName || roommate?.firstName || '';
  const last = roommate?.data?.account?.lastName || roommate?.lastName || '';
  const name = `${first} ${last}`.trim() || 'Roommate';
  const email = roommate?.data?.account?.email || roommate?.email || '';
  const phoneRaw = roommate?.data?.account?.phoneNumber || roommate?.data?.account?.phone || roommate?.phoneNumber || roommate?.phone || '';
  const unit = roommate?.data?.account?.unitNumberSpace || roommate?.data?.account?.unitNumber || roommate?.unitNumberSpace || roommate?.unitNumber || '';
  const bio = roommate?.data?.account?.roommateBio || roommate?.data?.account?.bio || roommate?.roommateBio || roommate?.bio || '';

  const initials = useMemo(() => {
    return `${first?.[0] || ''}${last?.[0] || ''}`;
  }, [first, last]);

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
      <View style={styles.header}>
        <View style={styles.avatarLarge}>
          <Text style={styles.avatarLargeText}>{initials}</Text>
        </View>
        <Text style={styles.name}>{name}</Text>
        {!!unit && <Text style={styles.sub}>{unit}</Text>}
      </View>

      <View style={styles.section}>
        {!!email && (
          <TouchableOpacity style={styles.row} onPress={() => Linking.openURL(`mailto:${email}`)}>
            <Text style={styles.label}>Email</Text>
            <Text style={styles.value}>{email}</Text>
          </TouchableOpacity>
        )}
        {!!phoneRaw && (
          <TouchableOpacity style={styles.row} onPress={() => Linking.openURL(`tel:${phoneRaw}`)}>
            <Text style={styles.label}>Phone</Text>
            <Text style={styles.value}>{phoneRaw}</Text>
          </TouchableOpacity>
        )}
      </View>

      {!!bio && (
        <View style={styles.section}>
          <Text style={styles.about}>About</Text>
          <Text scale="content" style={styles.bio}>{bio}</Text>
        </View>
      )}
    </ScrollView>
  );
};

const makeStyles = ({ colors, components }: ThemeContextValue) => StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: colors.bg.base,
  },
  container: {
    flexGrow: 1,
    backgroundColor: colors.bg.base,
    padding: 16,
  },
  header: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  avatarLarge: {
    height: 88,
    width: 88,
    borderRadius: 44,
    backgroundColor: components.avatar.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLargeText: {
    fontWeight: '700',
    color: colors.text.primary,
    fontSize: 28,
  },
  name: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text.primary,
    marginTop: 12,
  },
  sub: {
    fontSize: 13,
    color: colors.text.primary,
    opacity: 0.8,
    marginTop: 4,
  },
  section: {
    marginTop: 16,
  },
  about: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: 8,
  },
  bio: {
    fontSize: 14,
    color: colors.text.primary,
  },
  row: {
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.separator,
  },
  label: {
    fontSize: 13,
    color: colors.text.primary,
    opacity: 0.7,
    marginBottom: 4,
  },
  value: {
    fontSize: 15,
    color: colors.text.primary,
  },
});

export default RoommateDetailScreen;


